// Master-Hotfix P0 — manual data entry as a FIRST-CLASS fallback when a
// source blocks automated reads. Structured input card instead of a wall
// of text; submit turns the values into one structured chat message the
// AI feeds into the deterministic engines (NoVA, market, alternatives).
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, PencilLine, RefreshCw, ShieldAlert } from "lucide-react";

const HEADINGS = {
  blocked: "manualEntry.blockedHeading",
  extraction_failed: "manualEntry.extractionHeading",
  fetch_failed: "manualEntry.fetchHeading",
};

const REQUIRED = ["make", "model", "first_registration", "price", "origin_country"];

function Field({ field, value, onChange }) {
  const base =
    "w-full min-w-0 rounded-lg border border-zinc-800 bg-zinc-950/70 px-2.5 py-1.5 " +
    "text-sm text-zinc-100 placeholder-zinc-600 focus:border-amber-500/50 focus:outline-none";
  if (field.type === "select") {
    return (
      <select
        data-testid={`manual-entry-field-${field.key}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={base}
      >
        <option value="">—</option>
        {(field.options || []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (field.type === "textarea") {
    return (
      <textarea
        data-testid={`manual-entry-field-${field.key}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder={field.placeholder}
        className={`${base} resize-none`}
      />
    );
  }
  return (
    <input
      data-testid={`manual-entry-field-${field.key}`}
      type={field.type === "number" ? "number" : "text"}
      inputMode={field.type === "number" ? "decimal" : undefined}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      className={base}
    />
  );
}

export function ManualVehicleEntryCard({ widget, onSend }) {
  const { t } = useTranslation("ai");
  const [values, setValues] = useState({});
  const [sent, setSent] = useState(false);
  const fields = useMemo(() => widget.fields || [], [widget.fields]);

  const missingRequired = useMemo(
    () => REQUIRED.filter((k) => fields.some((f) => f.key === k))
      .filter((k) => !String(values[k] || "").trim()),
    [values, fields],
  );

  const submit = () => {
    if (missingRequired.length || !onSend || sent) return;
    const lines = fields
      .filter((f) => String(values[f.key] || "").trim())
      .map((f) => `${f.label.split(" (")[0]}: ${String(values[f.key]).trim()}`);
    const msg =
      `${t("manualEntry.messageIntro", { label: widget.short_label || "Link" })}` +
      ` (${widget.url}):\n${lines.join(", ")}.\n` +
      t("manualEntry.messageOutro");
    setSent(true);
    onSend(msg);
  };

  return (
    <div
      data-testid="manual-entry-card"
      className="min-w-0 max-w-full space-y-3 rounded-2xl border border-amber-500/25 bg-zinc-900/70 p-3.5"
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-100">
            {t(HEADINGS[widget.reason] || HEADINGS.blocked)}
          </div>
          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500">
            <span className="truncate" data-testid="manual-entry-source-label">
              {widget.short_label}
            </span>
            {widget.url && (
              <a
                href={widget.url}
                target="_blank"
                rel="noreferrer"
                data-testid="manual-entry-open-original"
                className="inline-flex items-center gap-1 text-amber-400/90 transition-colors hover:text-amber-300"
              >
                <ExternalLink className="h-3 w-3" />
                {t("manualEntry.openOriginal")}
              </a>
            )}
          </div>
          <p className="mt-1 text-xs text-zinc-400">{t("manualEntry.sub")}</p>
        </div>
      </div>

      {!sent && (
        <>
          <div className="grid min-w-0 grid-cols-2 gap-2">
            {fields.map((f) => (
              <label
                key={f.key}
                className={`min-w-0 space-y-1 ${f.type === "textarea" ? "col-span-2" : ""}`}
              >
                <span className="block text-[11px] text-zinc-500">
                  {f.label}
                  {f.required && <span className="text-amber-400"> *</span>}
                </span>
                <Field
                  field={f}
                  value={values[f.key]}
                  onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                />
              </label>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="manual-entry-submit"
              onClick={submit}
              disabled={missingRequired.length > 0}
              className="inline-flex items-center gap-1.5 rounded-xl bg-amber-500 px-3.5 py-2 text-xs font-medium text-zinc-950 transition hover:bg-amber-400 disabled:opacity-40"
            >
              <PencilLine className="h-3.5 w-3.5" />
              {t("manualEntry.submit")}
            </button>
            <button
              type="button"
              data-testid="manual-entry-retry"
              onClick={() => onSend && onSend(t("manualEntry.retryMessage", { url: widget.url }))}
              className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-700 px-3.5 py-2 text-xs text-zinc-300 transition hover:border-zinc-500 hover:text-zinc-100"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t("manualEntry.retry")}
            </button>
            {missingRequired.length > 0 && (
              <span className="text-[10px] text-zinc-600">
                {t("manualEntry.requiredHint")}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
